import { GridColDef } from "@mui/x-data-grid";

import { SortFilters } from "./types";

const findColumn = (columns: readonly GridColDef[], columnId: string): GridColDef | undefined =>
  columns.find((col) => {
    const generatedId = `column${col.headerName}-${col.field}`;
    return generatedId === columnId || col.field === columnId;
  });

const applyColumnFilters = (
  rows: any[],
  columnId: string,
  filterValues: string[],
  columns: readonly GridColDef[]
): any[] => {
  const column = findColumn(columns, columnId);
  if (!column) return rows;
  return rows.filter((row) => filterValues.includes(String(row[column.field] ?? "")));
};

const resolveSortConfig = (
  sortFilters: SortFilters,
  columns: readonly GridColDef[]
): { field: string; direction: "asc" | "desc" } | undefined => {
  let result: { field: string; direction: "asc" | "desc" } | undefined;
  for (const [columnId, filterData] of Object.entries(sortFilters)) {
    if (!filterData.sort) continue;
    const column = findColumn(columns, columnId);
    if (column) {
      result = { field: column.field, direction: filterData.sort === "desc" ? "desc" : "asc" };
    }
  }
  return result;
};

/**
 * Applies filters and sorting to an array of rows based on the provided sort filters configuration.
 *
 * @param rows - Array of row data to filter and sort
 * @param sortFilters - Configuration object containing filter and sort settings for each column
 * @param columns - Grid column definitions for mapping column IDs to fields
 * @returns Filtered and sorted array of rows
 */
export const applyFiltersAndSorting = (
  rows: any[],
  sortFilters: SortFilters,
  columns: readonly GridColDef[]
): any[] => {
  if (!rows || !columns) return rows;

  let filteredRows = [...rows];
  for (const [columnId, filterData] of Object.entries(sortFilters)) {
    if (filterData.filters && filterData.filters.length > 0) {
      filteredRows = applyColumnFilters(filteredRows, columnId, filterData.filters, columns);
    }
  }

  const sort = resolveSortConfig(sortFilters, columns);
  if (!sort) return filteredRows;

  return filteredRows.sort((a, b) => {
    const comparison = String(a[sort.field] ?? "").localeCompare(String(b[sort.field] ?? ""));
    return sort.direction === "desc" ? -comparison : comparison;
  });
};

/**
 * Creates a callback function for handling filter and sort updates.
 *
 * @param setSortFilters - State setter function for sortFilters
 * @returns Callback function that updates the sortFilters state
 */
export const createFilterSortHandler = (
  setSortFilters: React.Dispatch<React.SetStateAction<SortFilters>>
) => {
  return (columnId: string, sortValue: string | undefined, selectedFilters: string[]) => {
    setSortFilters((prev) => {
      const keys = Object.keys(prev);
      const modifiedPrev = {
        ...prev,
      };
      for (const key of keys) {
        if (sortValue) {
          modifiedPrev[key].sort = undefined;
        }
      }
      return {
        ...modifiedPrev,
        [columnId]: {
          sort: sortValue,
          filters: selectedFilters,
        },
      };
    });
  };
};

/**
 * Finds a column by its generated ID or field name.
 *
 * @param columns - Array of grid column definitions
 * @param columnId - The column ID to search for
 * @returns The matching column definition or undefined
 */
export const findColumnById = (
  columns: readonly GridColDef[],
  columnId: string
): GridColDef | undefined => {
  return columns.find((col) => {
    const generatedId = `column${col.headerName}-${col.field}`;
    return generatedId === columnId || col.field === columnId;
  });
};

/**
 * Generates a unique column ID based on header name and field name.
 *
 * @param headerName - The column header name
 * @param fieldName - The column field name
 * @returns Generated column ID string
 */
export const generateColumnId = (
  headerName: string | undefined,
  fieldName: string | undefined
): string => {
  return `column${headerName}-${fieldName}`;
};

/**
 * Creates functions for external sort and filter management.
 *
 * @param setSortFilters - State setter function for sortFilters
 * @returns Object with functions for managing filters and sorts externally
 */
export const createExternalFilterSortHandlers = (
  setSortFilters: React.Dispatch<React.SetStateAction<SortFilters>>
) => {
  /**
   * Applies sort and filter to a specific column from external components.
   *
   * @param columnId - The column identifier
   * @param sortValue - The sort direction ("asc", "desc", or undefined)
   * @param selectedFilters - Array of filter values to apply
   */
  const applyColumnSortFilter = (
    columnId: string,
    sortValue?: "asc" | "desc",
    selectedFilters?: string[]
  ) => {
    setSortFilters((prev) => ({
      ...prev,
      [columnId]: {
        sort: sortValue,
        filters: selectedFilters || [],
      },
    }));
  };

  /**
   * Clears all sorts and filters from all columns.
   */
  const clearAllSortFilters = () => {
    setSortFilters({});
  };

  /**
   * Removes sort and filter from a specific column.
   *
   * @param columnId - The column identifier to clear
   */
  const clearColumnSortFilter = (columnId: string) => {
    setSortFilters((prev) => {
      const newFilters = { ...prev };
      delete newFilters[columnId];
      return newFilters;
    });
  };

  return {
    applyColumnSortFilter,
    clearAllSortFilters,
    clearColumnSortFilter,
  };
};
